import {getSetting} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/settings';
import createAuth0Client, {Auth0Client, Auth0ClientOptions, RedirectLoginResult} from '@auth0/auth0-spa-js';
import moment from 'moment';

let _client: Auth0Client;

const getConfig = async (): Promise<Auth0ClientOptions> => ({
    audience: await getSetting('auth0_audience'),
    domain: await getSetting('auth0_domain'),
    client_id: await getSetting('auth0_client_id'),
});

const getClient = async (): Promise<Auth0Client> => {
    if (_client) {
        return Promise.resolve(_client);
    }

    if (window.auth0Client) {
        _client = window.auth0Client;
        return Promise.resolve(_client);
    }

    const config = await getConfig();
    _client = await createAuth0Client(config);

    return _client;
};

export const isAuthenticated = async (): Promise<boolean> => {
    const client = await getClient();
    return client.isAuthenticated();
};

export const loginWithRedirect = async (): Promise<void> => {
    const client = await getClient();
    return client.loginWithRedirect({redirect_uri: window.location.href});
};

export const handleRedirectCallback = async (): Promise<RedirectLoginResult> => {
    const client = await getClient();
    return client.handleRedirectCallback();
};

export const logout = async (): Promise<void> => {
    const client = await getClient();
    return Promise.resolve(client.logout({
        returnTo: `https://${window.location.host}`,
    }));
};

export const getTokenSilently = async (): Promise<string | undefined> => {
    const client = await getClient();
    return client.getTokenSilently();
};

export const setAuthorizationToken = async (accessToken = window.auth0AccessToken): Promise<void> => {
    const sessionTimeout = await getSetting('session_timeout') || 0;

    if (accessToken) {
        localStorage.setItem('auth0AccessTokenV1', accessToken);
        localStorage.setItem('auth0AccessTokenV2', accessToken);
    }

    if (sessionTimeout > 0) {
        const sessionExpiresTime = moment().add(sessionTimeout, 'seconds');
        localStorage.setItem('sessionExpiresTime', sessionExpiresTime.toISOString());
    }
};

export const handleAuth0Login = async (
    loginSuccessCallback: () => void,
    loginErrorCallback: (string, Object) => void,
): Promise<void> => {
    const urlParameters = new URLSearchParams(window.location.search);
    const successfulLoginRedirect = urlParameters.get('code') && urlParameters.get('state');
    const error = urlParameters.get('error');
    const errorDescription = urlParameters.get('error_description');

    try {
        if (await isAuthenticated()) {
            await setAuthorizationToken(await getTokenSilently());
            loginSuccessCallback();
        } else if (successfulLoginRedirect) {
            await handleRedirectCallback();
            await setAuthorizationToken(await getTokenSilently());

            window.history.replaceState(null, '', `/${window.location.hash}`);
            loginSuccessCallback();
        } else if (error) {
            loginErrorCallback(error, {errorDescription});
        } else {
            loginWithRedirect();
        }
    } catch (err) {
        loginErrorCallback('login_error', {errorName: err.name, errorMessage: err.message});
        console.error(err);
    }
};
